import { AxiosResponse } from 'axios'
import { IRequisiteHistoryErrors } from './error.types'

export const getErrorResponse = <T>(error: unknown): T | null => {
  return isApiErrorResponse<T>(error) ? error.data.errors : null
}

const isApiErrorResponse = <T>(error: any): error is AxiosResponse<IRequisiteHistoryErrors<T>> => {
  return typeof error === 'object' && error !== null && error?.data?.errors
}

/**
 * Машиночитаемый код доменной ошибки (CPAYMENT-1591).
 *
 * Читаем из `error.data`, а НЕ из `error.response.data`: глобальный перехватчик
 * (`setupAxios` в `auth/core/AuthHelpers.ts`) реджектит сам `response`, поэтому `error.response`
 * в catch страницы всегда `undefined`. Обращение через `.response` тихо даёт `undefined`, и
 * страница показывает generic-фолбэк вместо текста по коду.
 */
export const getErrorCode = (error: unknown): string | undefined => {
  const data = (error as AxiosResponse | undefined)?.data
  const code = (data as { errorCode?: unknown } | undefined)?.errorCode

  return typeof code === 'string' ? code : undefined
}

/** The backend's sentence for the refusal (`errors.general_error`); undefined for a field-validation error. */
export const getGeneralError = (error: unknown): string | undefined => {
  const value = getErrorResponse<{ general_error?: unknown }>(error)?.general_error

  return typeof value === 'string' && value !== '' ? value : undefined
}

/**
 * Field errors of a validation refusal (400) for a form to show under its fields; the sentence and the context
 * keys of other refusals are not fields.
 */
export const getFieldErrors = (error: unknown): Record<string, string> => {
  const errors = getErrorResponse<unknown>(error)
  if (getErrorStatus(error) !== 400 || !errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return {}
  }

  return Object.fromEntries(
    Object.entries(errors as Record<string, unknown>).filter(
      (entry): entry is [string, string] => entry[0] !== 'general_error' && typeof entry[1] === 'string'
    )
  )
}

/** HTTP status of what the interceptor rejected with; undefined for a network error or a thrown `Error`. */
export const getErrorStatus = (error: unknown): number | undefined => {
  const status = (error as { status?: unknown } | null | undefined)?.status

  return typeof status === 'number' ? status : undefined
}

/** The role may not do this — HTTP 403; the same words as the common alert. */
export const FORBIDDEN_TEXT = 'У вас нет доступа к данному действию'

/** The backend's generic English text of a code: it says no more than the code itself, so it is not shown. */
const CATALOG_TEXTS: Record<string, string> = { AUTH_FORBIDDEN: 'Access denied' }

/**
 * The backend's reason for a 403 worth showing after `FORBIDDEN_TEXT`; undefined when it says no more than the
 * fixed text or the code's generic English text.
 */
export const getForbiddenReason = (error: unknown): string | undefined => {
  const reason = getGeneralError(error)
  const code = getErrorCode(error)
  const saysNothingMore = reason === FORBIDDEN_TEXT || (code !== undefined && reason === CATALOG_TEXTS[code])

  return saysNothingMore ? undefined : reason
}

/** A dependency of the backend (DB, Ledger, matching, file storage, LLM) is unreachable — HTTP 503. */
export const SERVICE_UNAVAILABLE_TEXT = 'Сервис временно недоступен, повторите позже'

interface ErrorTextOptions {
  /** The page's own texts by `errorCode`; they win over the backend sentence. */
  labels?: Record<string, string>
  /** Prefix a field error with its field name («title: …»); off for screens that show only the sentence. */
  withFieldName?: boolean
}

/**
 * Text of a refusal for a page that shows it itself, for the new body and for legacy ones alike: the page's
 * label of the code, the backend sentence, the first field error, the fallback. A refusal of the role shows the
 * fixed 403 text with the backend reason after it (not the code's generic text), a server failure the fallback
 * (its English text is not for the user), an unreachable dependency the fixed 503 text. Works on a resolved
 * `success: false` response too.
 */
export const getErrorText = (
  error: unknown,
  fallback: string,
  { labels = {}, withFieldName = true }: ErrorTextOptions = {}
): string => {
  const code = getErrorCode(error)
  if (code && labels[code]) {
    return labels[code]
  }

  const status = getErrorStatus(error)
  if (status === 403) {
    // the fixed lead, as in the common alert, and the backend's reason as the detail
    const reason = getForbiddenReason(error)

    return reason ? `${FORBIDDEN_TEXT}: ${reason}` : FORBIDDEN_TEXT
  }
  if (status === 503) {
    return SERVICE_UNAVAILABLE_TEXT
  }
  if (status !== undefined && status >= 500) {
    return fallback
  }

  const generalError = getGeneralError(error)
  if (generalError) {
    return generalError
  }

  const errors = getErrorResponse<unknown>(error)
  if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
    for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
      if (typeof value === 'string' && value !== '') {
        // Legacy bodies put a sentence under `error`; it is not a field.
        return withFieldName && key !== 'error' ? `${key}: ${value}` : value
      }
    }
  }

  return fallback
}
